import React from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { Button } from "@/components/ui/Button";
import { Modal as UIModal } from "@/components/ui/Modal";
import type { FieldEditData } from "./Shared";

export interface FieldShowEditModalProps {
  visible: boolean;
  onDismiss: () => void;
  /** Called when "Update" is pressed — typically re-opens the EditFieldModal. */
  onUpdate: () => void;
  /** Called when "Undo Edit" is pressed — clears the edit decision. */
  onUndoEdit: () => void;
  /** The translated field label shown as the modal heading. */
  fieldLabel?: string;
  data?: FieldEditData;
  t: ReturnType<typeof import("@/context/I18nContext").useI18n>["t"];
}

export function FieldShowEditModal({
  visible,
  onDismiss,
  onUpdate,
  onUndoEdit,
  fieldLabel,
  data,
  t,
}: FieldShowEditModalProps) {
  return (
    <UIModal
      visible={visible}
      onDismiss={onDismiss}
      title={null}
      closeOnBackdrop
      useScrollView
      footer={
        <View className="flex-row items-center justify-end gap-4">
          <Button
            label="review.review.fieldState.showEditModal.update"
            variant="outline"
            onPress={onUpdate}
          />
          <Button
            label="review.review.fieldState.showEditModal.undoEdit"
            variant="destructive"
            onPress={onUndoEdit}
          />
        </View>
      }
    >
      {/* Dynamic field label as modal heading */}
      {fieldLabel && (
        <AppText weight="semibold" className="text-lg text-dark-text-50 mb-4">
          {fieldLabel}
        </AppText>
      )}

      {/* Current Value (the new value entered by the reviewer) */}
      <View className="gap-2 py-2">
        <AppText weight="medium" className="text-base text-supporting-text-40">
          {t("review.review.fieldState.showEditModal.currentValue")}
        </AppText>
        <AppText weight="medium" className="text-base text-dark-text-50">
          {data?.newValue ?? "—"}
        </AppText>
      </View>

      {/* Divider */}
      <View className="h-px bg-stroke-20 my-2" />

      {/* Previous Value (the original application value) */}
      <View className="gap-2 py-2">
        <AppText weight="medium" className="text-base text-supporting-text-40">
          {t("review.review.fieldState.showEditModal.previousValue")}
        </AppText>
        <AppText weight="medium" className="text-base text-dark-text-50">
          {data?.previousValue ?? "—"}
        </AppText>
      </View>
    </UIModal>
  );
}
